import { DocumentExports } from '@/features/rendering/DocumentExports';
import { useOrgTimezone } from '@/lib/timezone';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions/hooks/use-permissions';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { GeneratedDocumentOut } from '../api/documents-api';
import { SendToBiolockrButton, SendToBiolockrDialog } from './SendToBiolockrDialog';

type Snapshot = {
  signature_text?: string;
  content: string;
  title: string;
  patient_name: string;
  medical_record_number: string;
  date_of_birth: string | null;
};
export type SignedVersion = {
  id: string;
  version_number: number;
  signed_at: string;
  signed_by: string;
  signer_name: string;
  signer_credentials: string;
  snapshot: Snapshot;
  amendment_reason: string | null;
};
type Preview = {
  snapshot: Snapshot;
  expected_hash: string;
  image_url: string;
  attestation: string;
  signer_name: string;
  signer_credentials: string;
  amendment_reason: string | null;
};
type Amendment = { id: string; content: string; reason: string; revision: number; created_by: string };
export function signingError(error: unknown): string {
  const e = error as { response?: { data?: { message?: string; detail?: string } } };
  return (
    e.response?.data?.message ||
    (typeof e.response?.data?.detail === 'string'
      ? e.response.data.detail
      : 'The request failed. Reload and try again.')
  );
}
export function ReportSigningPanel({
  document,
  dirty,
  onChanged,
}: {
  document: GeneratedDocumentOut;
  dirty: boolean;
  onChanged: () => void;
}) {
  const { user } = useAuth();
  const { timeZone } = useOrgTimezone();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const base = `/api/v1/organizations/${document.organization_id}/documents/${document.id}`;
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [requestKey, setRequestKey] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [biolockrOpen, setBiolockrOpen] = useState(false);
  const [selected, setSelected] = useState<string>('');
  const [amendmentOpen, setAmendmentOpen] = useState(false);
  const [draft, setDraft] = useState<Amendment | null>(null);
  const [reason, setReason] = useState('');
  const [content, setContent] = useState('');
  const { data: versions = [], refetch } = useQuery<SignedVersion[]>({
    queryKey: ['report-versions', document.organization_id, document.id],
    queryFn: async () => (await clientApi.get(`${base}/versions`)).data,
  });
  const { data: capabilities } = useQuery<{ can_sign: boolean; can_amend: boolean }>({
    queryKey: ['signing-capabilities', document.organization_id],
    queryFn: async () =>
      (await clientApi.get(`/api/v1/organizations/${document.organization_id}/signing-capabilities`)).data,
  });
  const latest = versions[0];
  const viewing = versions.find((v) => v.id === selected) || latest;
  const previous = viewing && versions.find((v) => v.version_number === viewing.version_number - 1);
  const locked = !!document.signed_version_id || !!latest;
  const stamp = (value: string) =>
    new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'long', timeZone }).format(new Date(value));
  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      toast.error(signingError(e));
    } finally {
      setBusy(false);
    }
  }
  async function review() {
    await run(async () => {
      setPreview((await clientApi.get(`${base}/signing-preview`)).data);
      setRequestKey(crypto.randomUUID());
    });
  }
  async function sign() {
    if (!preview) return;
    await run(async () => {
      await clientApi.post(`${base}/sign`, {
        expected_hash: preview.expected_hash,
        idempotency_key: requestKey,
        attestation: preview.attestation,
      });
      setPreview(null);
      setAmendmentOpen(false);
      setDraft(null);
      await refetch();
      await queryClient.invalidateQueries();
      onChanged();
      toast.success('Report signed and locked');
    });
  }
  async function openAmendment() {
    await run(async () => {
      const saved: Amendment | null = (await clientApi.get(`${base}/amendment`)).data;
      setDraft(saved);
      setReason(saved?.reason || '');
      setContent(saved?.content || latest.snapshot.content);
      setAmendmentOpen(true);
    });
  }
  async function saveDraft() {
    let saved = draft;
    if (!saved) saved = (await clientApi.post(`${base}/amendment`, { reason, parent_version_id: latest.id })).data;
    const result = await clientApi.patch(`${base}/amendment`, { content, reason, expected_revision: saved!.revision });
    setDraft(result.data);
    onChanged();
    toast.success('Amendment draft saved. The signed report is unchanged.');
  }
  async function download(id: string) {
    await run(async () => {
      const result = await clientApi.get(`${base}/versions/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(result.data);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `signed-report-${id}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });
  }
  return (
    <div className="flex flex-col gap-2">
      {can('export_document') && (
        <DocumentExports
          key={latest?.id || document.content}
          base={base}
          versionId={latest?.id}
          disabled={dirty || document.status === 'generating'}
        />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">
          {locked ? `Signed version ${latest?.version_number || document.version_number}` : 'Draft — unsigned'}
        </Badge>
        {latest && (
          <span className="text-sm text-muted-foreground">
            {latest.signer_name} · {stamp(latest.signed_at)}
          </span>
        )}
        {!locked && capabilities?.can_sign && (
          <Button size="sm" disabled={busy || dirty || document.status === 'generating'} onClick={review}>
            Review and sign
          </Button>
        )}
        {locked && latest?.signed_by === user?.id && capabilities?.can_amend && (
          <Button size="sm" variant="outline" disabled={busy} onClick={openAmendment}>
            Create or resume amendment
          </Button>
        )}
        {locked && (
          <Button size="sm" variant="outline" onClick={() => setHistoryOpen(true)}>
            Version history
          </Button>
        )}
        {locked && document.patient_id && document.signed_version_id && (
          <SendToBiolockrButton onClick={() => setBiolockrOpen(true)} />
        )}
      </div>
      {document.amendment_in_progress && (
        <p className="text-sm text-muted-foreground">Amendment in progress. The last signed version remains current.</p>
      )}
      {dirty && <p className="text-sm text-muted-foreground">Save your changes before signing.</p>}
      {document.patient_id && (
        <SendToBiolockrDialog
          open={biolockrOpen}
          patientId={document.patient_id}
          preselect={{ kind: 'report', id: document.id }}
          onOpenChange={setBiolockrOpen}
        />
      )}
      <Dialog
        open={!!preview}
        onOpenChange={(open) => {
          if (!open && !busy) setPreview(null);
        }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Sign and lock report</DialogTitle>
            <DialogDescription>
              Review this exact version. Signing permanently locks it; further changes require an amendment.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <>
              <p>
                {preview.snapshot.patient_name} · MRN {preview.snapshot.medical_record_number} · DOB{' '}
                {preview.snapshot.date_of_birth || 'Not recorded'}
              </p>
              <p>{preview.snapshot.title}</p>
              {preview.amendment_reason && <p>Amendment reason: {preview.amendment_reason}</p>}
              <pre className="whitespace-pre-wrap break-words rounded-md border p-4 text-sm">
                {preview.snapshot.content}
              </pre>
              <img className="max-h-24 max-w-64 object-contain" src={preview.image_url} alt="Your signature" />
              {preview.snapshot.signature_text && (
                <p className="whitespace-pre-wrap">{preview.snapshot.signature_text}</p>
              )}
              <p>
                {preview.signer_name}, {preview.signer_credentials}
              </p>
              <p>{preview.attestation}</p>
              <DialogFooter>
                <Button variant="outline" disabled={busy} onClick={() => setPreview(null)}>
                  Cancel
                </Button>
                <Button disabled={busy} onClick={sign}>
                  {busy ? 'Signing…' : 'I attest — sign and lock'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={amendmentOpen}
        onOpenChange={(open) => {
          if (!busy) setAmendmentOpen(open);
        }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Amend signed report</DialogTitle>
            <DialogDescription>
              Version {latest?.version_number} remains signed and visible until this amendment is signed. Save before
              closing.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="amendment-reason">Amendment reason</FieldLabel>
              <Input
                id="amendment-reason"
                value={reason}
                maxLength={2000}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="amendment-content">Amended report</FieldLabel>
              <Textarea
                id="amendment-content"
                className="min-h-80"
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={busy || reason.trim().length < 3 || !content.trim()}
              onClick={() => run(saveDraft)}>
              Save draft
            </Button>
            <Button
              disabled={busy || reason.trim().length < 3 || !content.trim()}
              onClick={() =>
                run(async () => {
                  await saveDraft();
                  setPreview((await clientApi.get(`${base}/signing-preview`)).data);
                  setRequestKey(crypto.randomUUID());
                })
              }>
              Save and review signature
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Signed version history</DialogTitle>
            <DialogDescription>
              Every signed version is preserved. Compare the selected version with its predecessor.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {versions.map((v) => (
              <Button
                key={v.id}
                variant={viewing?.id === v.id ? 'default' : 'outline'}
                onClick={() => setSelected(v.id)}>
                Version {v.version_number}
              </Button>
            ))}
          </div>
          {viewing && (
            <>
              <p>
                {viewing.signer_name}, {viewing.signer_credentials} · {stamp(viewing.signed_at)}
              </p>
              <p>{viewing.amendment_reason || 'Original signed report'}</p>
              <div className="grid gap-4 md:grid-cols-2">
                {previous && (
                  <div>
                    <p>Previous: version {previous.version_number}</p>
                    <pre className="whitespace-pre-wrap break-words rounded-md border p-3 text-sm">
                      {previous.snapshot.content}
                    </pre>
                  </div>
                )}
                <div>
                  <p>Selected: version {viewing.version_number}</p>
                  <pre className="whitespace-pre-wrap break-words rounded-md border p-3 text-sm">
                    {viewing.snapshot.content}
                  </pre>
                </div>
              </div>
              {can('export_document') && (
                <>
                  <Button disabled={busy} onClick={() => download(viewing.id)}>
                    Download archived copy of this version
                  </Button>
                  <DocumentExports key={viewing.id} base={base} versionId={viewing.id} />
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
